import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Store } from '@ngrx/store';
import { UserDataService, VCardGenerator } from '@planone/bau365/util-user-data';
import { CountriesFacade } from '@planone/shared/data-access-countries';
import * as DataAccessCountriesActions from '@planone/shared/data-access-countries';
import { DownloadFileGenerator } from '@planone/shared/util-downloads';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import {
  downloadBusinessCard,
  enrichBusinessCard,
  enrichBusinessCardFailure,
  enrichBusinessCardSuccess,
  loadBusinessCard,
  loadBusinessCardFailure,
  loadBusinessCardSuccess,
  loadBusinessCardUnknown,
} from './business-card.actions';

import { BusinessCardService } from '../service/business-card.service';

@Injectable()
export class BusinessCardEffects {
  loadBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBusinessCard),
      switchMap(({ id }) =>
        this.service.loadBusinessCard(id).pipe(
          map((businessCard) =>
            typeof businessCard === 'undefined' ? loadBusinessCardUnknown() : loadBusinessCardSuccess({ businessCard })
          ),
          catchError((error) => of(loadBusinessCardFailure({ error })))
        )
      )
    )
  );

  businessCardFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBusinessCardSuccess),
      map(({ businessCard }) => enrichBusinessCard({ businessCard }))
    )
  );

  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBusinessCard),
      withLatestFrom(of(this.localeId)),
      map(([_, localeId]) => (localeId === 'de' ? 'de' : 'en')),
      map((localeId) => DataAccessCountriesActions.initCountries({ localeId }))
    )
  );

  enrichBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enrichBusinessCard),
      withLatestFrom(this.countriesFacade.allCountries$),
      switchMap(([{ businessCard }, countries]) =>
        this.userDataService
          .getEmployeeCountOptions()
          .pipe(map((employeeCountOptions) => ({ businessCard, countries, employeeCountOptions })))
      ),
      switchMap(({ businessCard, countries, employeeCountOptions }) =>
        this.userDataService
          .getAreaOfInterestOptions()
          .pipe(
            map((areaOfInterestOptions) => ({ businessCard, countries, employeeCountOptions, areaOfInterestOptions }))
          )
      ),
      switchMap(({ businessCard, countries, employeeCountOptions, areaOfInterestOptions }) =>
        this.service.enrichBusinessCard(businessCard, countries, employeeCountOptions, areaOfInterestOptions).pipe(
          map((businessCard) => enrichBusinessCardSuccess({ businessCard })),
          catchError((error) => of(enrichBusinessCardFailure({ error })))
        )
      )
    )
  );

  downloadBusinessCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(downloadBusinessCard),
        switchMap(({ businessCard }) =>
          this.vcf.generateVCard(businessCard).pipe(
            tap((content) =>
              this.downloads.downloadToFile({
                content,
                type: 'text/vcard',
                name: $localize`:@@featureBusinessCard-download-file:Visitenkarte ${businessCard.first_name} ${businessCard.last_name}`,
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private service: BusinessCardService,
    private vcf: VCardGenerator,
    private downloads: DownloadFileGenerator,
    private readonly countriesFacade: CountriesFacade,
    private userDataService: UserDataService,
    @Inject(LOCALE_ID) private localeId: string
  ) {}
}
